<template>
  <div>
    <!-- List views: sidebar layout -->
    <section v-if="isListView" class="dir-layout">
      <CatalogueSidebar
        :activeTab="activeTab"
        :productCount="productCount"
        :categoryCount="categoryCount"
        :inventoryLocked="inventoryLocked"
        @change-tab="onChangeTab"
      />

      <div class="dir-content">
        <TopBarTitleComponent title="Catálogo" :showBackIcon="true">
          <template slot="right-area">
            <div class="d-flex justify-content-end align-items-center">
              <ButtonPrimaryComponent
                v-if="activeTab === 'products' && $can('products.manage')"
                title="Nuevo Producto"
                @onSaveClick="goRouter('catalogue.products.add')"
              />
              <ButtonPrimaryComponent
                v-else-if="
                  activeTab === 'categories' && $can('categories.manage')
                "
                title="Nueva Categoría"
                @onSaveClick="goRouter('catalogue.categories.add')"
              />
            </div>
          </template>
        </TopBarTitleComponent>

        <div class="dir-content-body">
          <!-- Header subtitle -->
          <p
            class="dir-header-subtitle d-block d-lg-none"
            style="margin: 0 0 12px;"
          >
            {{ productCount }} productos · {{ categoryCount }} categorías
          </p>

          <!-- Pill tabs (mobile only: replaces sidebar) -->
          <div class="dir-tabs d-lg-none">
            <button
              v-for="tab in tabs"
              :key="tab.key"
              class="dir-tab"
              :class="{ active: activeTab === tab.key }"
              @click="onChangeTab(tab.key)"
            >
              {{ tab.label }}
            </button>
          </div>

          <!-- Content area: renders the current list -->
          <div
            v-if="activeTab === 'movements' && inventoryLocked"
            class="catalogue-upgrade-wrap"
          >
            <div class="report-upgrade-card">
              <div class="report-upgrade-card__icon">
                <svg
                  width="26"
                  height="26"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#3ecfb4"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <rect
                    x="3"
                    y="11"
                    width="18"
                    height="11"
                    rx="2"
                    ry="2"
                  ></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
              </div>
              <h3 class="report-upgrade-card__title">Gestión de Inventario</h3>
              <p class="report-upgrade-card__desc">
                Control de stock, movimientos y trazabilidad de inventario.
                Disponible en el plan Escala o superior.
              </p>
              <router-link
                :to="{ name: 'plans' }"
                class="report-upgrade-card__btn"
                >Ver planes</router-link
              >
            </div>
          </div>
          <router-view
            v-else
            @update-product-count="onProductCountUpdate"
            @update-category-count="onCategoryCountUpdate"
          />
        </div>
      </div>
    </section>

    <!-- Non-list views (Add, Edit, Detail): full width, no sidebar -->
    <router-view v-else class="content" />
  </div>
</template>

<script>
import ButtonPrimaryComponent from "../../components/ButtonPrimary";
import TopBarTitleComponent from "../../components/TopBarTitle";
import CatalogueSidebar from "./CatalogueSidebar";
import catalogueService from "../services/catalogueService";

const LIST_ROUTES = [
  "catalogue.products",
  "catalogue.categories",
  "catalogue.movements",
  "catalogue.attributes"
];

const TAB_ROUTE_MAP = {
  products: "catalogue.products",
  categories: "catalogue.categories",
  movements: "catalogue.movements"
};

export default {
  name: "CatalogueMainComponent",
  components: {
    ButtonPrimaryComponent,
    TopBarTitleComponent,
    CatalogueSidebar
  },
  data() {
    return {
      productCount: 0,
      categoryCount: 0
    };
  },
  computed: {
    inventoryLocked() {
      return !this.$store.getters.hasFeature("inventory_management");
    },
    isListView() {
      return LIST_ROUTES.includes(this.$route.name);
    },
    activeTab() {
      const name = this.$route.name;
      if (name === "catalogue.products") return "products";
      if (name === "catalogue.categories") return "categories";
      if (name === "catalogue.movements" || name === "catalogue.attributes")
        return "movements";
      return "products";
    },
    tabs() {
      return [
        { key: "products", label: "Productos" },
        { key: "categories", label: "Categorías" },
        { key: "movements", label: "Inventario" }
      ];
    }
  },
  methods: {
    onChangeTab(tab) {
      if (tab === "movements" && this.inventoryLocked) {
        this.$router.push({ name: "catalogue.movements" }).catch(() => {});
        return;
      }
      const routeName = TAB_ROUTE_MAP[tab];
      if (routeName && this.$route.name !== routeName) {
        this.$router.push({ name: routeName }).catch(() => {});
      }
    },
    goRouter(name, params = {}) {
      this.$router.push({ name, params });
    },
    onProductCountUpdate(count) {
      this.productCount = count;
    },
    onCategoryCountUpdate(count) {
      this.categoryCount = count;
    },
    async fetchCounts() {
      try {
        const [prodRes, catRes] = await Promise.all([
          catalogueService.getProducts({ params: { page: 1 } }),
          catalogueService.getCategories()
        ]);
        if (prodRes?.data?.pagination?.total) {
          this.productCount = prodRes.data.pagination.total;
        } else if (prodRes?.data?.products) {
          this.productCount = prodRes.data.products.length;
        }
        if (catRes?.data?.categories) {
          this.categoryCount = catRes.data.categories.length;
        }
      } catch {
        // silently fail
      }
    }
  },
  mounted() {
    this.fetchCounts();
  }
};
</script>

<style lang="scss" scoped>
.dir-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.dir-header-actions {
  flex-shrink: 0;
}

.catalogue-upgrade-wrap {
  padding: 32px 0;
}

.report-upgrade-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 48px 24px;
  background: #fff;
  border: 1.5px dashed #d0ede8;
  border-radius: 12px;
  gap: 10px;

  &__icon {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #f0faf7;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 4px;
  }

  &__title {
    font-size: 17px;
    font-weight: 700;
    color: #2d2b55;
    margin: 0;
  }

  &__desc {
    font-size: 13px;
    color: #888780;
    margin: 0;
    max-width: 380px;
    line-height: 1.5;
  }

  &__btn {
    margin-top: 6px;
    padding: 9px 22px;
    background: #2d2b55;
    color: #fff;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    display: inline-block;
    transition: background 0.15s;

    &:hover {
      background: #3ecfb4;
      color: #fff;
      text-decoration: none;
    }
  }
}
</style>
